import TrackingService from './TrackingService';

class VisitorIdentificationService {
  constructor() {
    this.visitorProfile = {};
    this.identificationMethods = {
      cookies: true,
      fingerprinting: true,
      socialLogin: true,
      crossDomainTracking: true
    };
    
    this.initialize();
  }
  
  initialize() {
    // Extract data from various sources
    this.extractCookieData();
    this.extractLocalStorageData();
    this.detectSocialLogins();
    this.performBrowserFingerprinting();
    this.setupLoginCallbacks();
    
    // Send initial profile data
    this.sendProfileData();
    
    // Set up periodic profile updates
    setInterval(() => this.updateVisitorProfile(), 60000); // Every minute
  }
  
  extractCookieData() {
    try {
      // Look for common authentication cookies
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
      }, {});
      
      // Check for common auth cookies
      if (cookies['_li_ss']) this.visitorProfile.linkedInConnected = true;
      if (cookies['c_user'] || cookies['xs']) this.visitorProfile.facebookConnected = true;
      if (cookies['SID'] || cookies['HSID'] || cookies['SSID']) this.visitorProfile.googleConnected = true;
      
      // Extract UTM parameters if available
      const utmSource = this.getCookie('utm_source');
      const utmMedium = this.getCookie('utm_medium');
      const utmCampaign = this.getCookie('utm_campaign');
      
      if (utmSource || utmMedium || utmCampaign) {
        this.visitorProfile.utmData = {
          source: utmSource,
          medium: utmMedium,
          campaign: utmCampaign,
          term: this.getCookie('utm_term'),
          content: this.getCookie('utm_content')
        };
      }
    } catch (error) {
      console.warn('Error extracting cookie data:', error);
    }
  }
  
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }
  
  extractLocalStorageData() {
    try {
      // Check for auth tokens or user data in localStorage
      const authToken = localStorage.getItem('token');
      const userData = localStorage.getItem('userData');
      
      if (authToken) this.visitorProfile.authenticated = true;
      if (userData) {
        try {
          const parsedUserData = JSON.parse(userData);
          this.visitorProfile.userId = parsedUserData.id;
          this.visitorProfile.userRole = parsedUserData.role;
          this.visitorProfile.userType = parsedUserData.type;
        } catch (e) {
          console.warn('Error parsing user data:', e);
        }
      }
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }
  }
  
  detectSocialLogins() {
    // Check for social login scripts and APIs
    if (window.FB) this.visitorProfile.facebookSDKLoaded = true;
    if (window.gapi) this.visitorProfile.googleSDKLoaded = true;
    if (window.IN || window.LinkedIn) this.visitorProfile.linkedInSDKLoaded = true;
    
    // Check for common social login elements
    setTimeout(() => {
      const facebookButtons = document.querySelectorAll('[data-provider="facebook"]');
      const googleButtons = document.querySelectorAll('[data-provider="google"]');
      const linkedInButtons = document.querySelectorAll('[data-provider="linkedin"]');
      
      if (facebookButtons.length > 0) this.visitorProfile.hasFacebookLogin = true;
      if (googleButtons.length > 0) this.visitorProfile.hasGoogleLogin = true;
      if (linkedInButtons.length > 0) this.visitorProfile.hasLinkedInLogin = true;
    }, 2000);
  }
  
  performBrowserFingerprinting() {
    // Create a simple fingerprint based on browser properties
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: window.screen.colorDepth,
      platform: navigator.platform,
      doNotTrack: navigator.doNotTrack,
      cookiesEnabled: navigator.cookieEnabled,
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      indexedDB: !!window.indexedDB,
      plugins: this.getPluginsData(),
      fonts: this.detectCommonFonts(),
      canvas: this.getCanvasFingerprint()
    };
    
    // Generate a hash from the fingerprint
    this.visitorProfile.fingerprint = this.hashObject(fingerprint);
    this.visitorProfile.fingerprintData = fingerprint;
  }
  
  getPluginsData() {
    if (!navigator.plugins) return [];
    
    const pluginsData = [];
    for (let i = 0; i < navigator.plugins.length; i++) {
      const plugin = navigator.plugins[i];
      pluginsData.push({
        name: plugin.name,
        description: plugin.description
      });
    }
    return pluginsData;
  }
  
  detectCommonFonts() {
    const fontList = [
      'Arial', 'Courier New', 'Georgia', 'Times New Roman', 
      'Verdana', 'Helvetica', 'Calibri', 'Tahoma', 'Impact'
    ];
    
    const detectedFonts = [];
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const baseFont = 'monospace';
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) return [];
    
    context.font = testSize + ' ' + baseFont;
    const baseWidth = context.measureText(testString).width;
    
    for (const font of fontList) {
      try {
        context.font = testSize + ' ' + font + ', ' + baseFont;
        const width = context.measureText(testString).width;
        if (width !== baseWidth) {
          detectedFonts.push(font);
        }
      } catch (e) {
        // Font detection failed
      }
    }
    
    return detectedFonts;
  }
  
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      
      canvas.width = 200;
      canvas.height = 50;
      
      // Text with different styles
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#F60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Fingerprint', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Canvas', 4, 17);
      
      // Add a gradient
      const gradient = ctx.createLinearGradient(0, 0, 200, 0);
      gradient.addColorStop(0, 'blue');
      gradient.addColorStop(1, 'red');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 25, 200, 25);
      
      return canvas.toDataURL().slice(-50);
    } catch (e) {
      return '';
    }
  }
  
  hashObject(obj) {
    const str = JSON.stringify(obj);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(36);
  }
  
  setupLoginCallbacks() {
    // Listen for login events
    document.addEventListener('login', (e) => {
      if (e.detail && e.detail.provider) {
        this.handleSocialLogin(e.detail.provider, e.detail.userData);
      }
    });
    
    // Monitor for changes in authentication state
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      
      if (key === 'token' || key === 'userData') {
        document.dispatchEvent(new Event('storageChange'));
      }
    };
    
    document.addEventListener('storageChange', () => {
      this.extractLocalStorageData();
      this.sendProfileData();
    });
  }
  
  handleSocialLogin(provider, userData) {
    this.visitorProfile.authenticated = true;
    this.visitorProfile.authProvider = provider;
    
    if (userData) {
      if (userData.email) this.visitorProfile.email = userData.email;
      if (userData.name) this.visitorProfile.name = userData.name;
      if (userData.id) this.visitorProfile.socialId = userData.id;
      if (userData.picture) this.visitorProfile.profilePicture = userData.picture;
    }
    
    // Update profile with social data
    this.sendProfileData();
  }
  
  updateVisitorProfile() {
    // Check for changes in visitor data
    this.extractCookieData();
    this.extractLocalStorageData();
    
    // Update engagement metrics
    this.visitorProfile.timeOnSite = (Date.now() - TrackingService.sessionStartTime) / 1000;
    this.visitorProfile.pageViews = TrackingService.pageViewCount || 0;
    
    // Send updated profile
    this.sendProfileData();
  }
  
  sendProfileData() {
    // Add timestamp
    this.visitorProfile.lastUpdated = new Date().toISOString();
    
    // Send to tracking service
    TrackingService.trackEvent('visitor_profile_update', {
      profile: this.visitorProfile
    });
  }
  
  // Public methods for external use
  getVisitorProfile() {
    return { ...this.visitorProfile };
  }
  
  enrichWithExternalData(externalData) {
    this.visitorProfile = {
      ...this.visitorProfile,
      ...externalData
    };
    this.sendProfileData();
  }
}

export default new VisitorIdentificationService(); 